
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Divider,
  Stack,
} from "@mui/material";
import CustomTypography from "components/Fileds/CustomTypography";
import { useSelector } from "react-redux";
import { currencySymbol } from "utils/constant";
import CustomButton from "components/Fileds/CustomButton";
import CustomTextField from "components/Fileds/CustomTextFiled";
import { useAddDiscountOrderMutation } from "pages/orders/services";
import { calculateDiscount } from "utils/function";
import { focusFirstField } from "utils/focusHelper";

const DiscountModal = ({ open, onClose, orderDetails, onApplyDiscount, orderItemTotal }) => {
  const vendorData = useSelector((state) => state?.posData?.vendor);
  const [mode, setMode] = useState("percentage"); // "percentage" or "amount"
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [customValueInput, setCustomValueInput] = useState("");
  const [customValue, setCustomValue] = useState("");
  const [discountError, setDiscountError] = useState("");
  const [addDiscountOrderAPI] = useAddDiscountOrderMutation();

  // static options
  const percentageOptions = [5, 10, 15, 20];
  const amountOptions = [2, 5, 10, 15];

  // set default selection when mode changes
  useEffect(() => {
    if (mode === "percentage") {
      setSelectedDiscount(percentageOptions[0]);
    } else {
      setSelectedDiscount(amountOptions[0]);
    }
    setCustomValue("");
    setCustomValueInput("");
  }, [mode]);

  // Calculate discount
  const getDiscountAmount = () => {
    if (mode === "percentage") {
      if (selectedDiscount) return (orderItemTotal * selectedDiscount) / 100;
      if (customValue) return (orderItemTotal * Number(customValue)) / 100;
    } else {
      if (selectedDiscount) return selectedDiscount;
      if (customValue) return Number(customValue);
    }
    return 0;
  };

  const discountAmount = getDiscountAmount();
  const finalTotal = Math.max(orderItemTotal - discountAmount, 0);

  const handleApply = async () => {

    if (discountAmount > orderItemTotal) {
      setDiscountError("Discount amount cannot be greater than the order items total.");
      return;
    }

    setDiscountError("");

    const cartItemTotal = orderItemTotal;
    const isCustomerOrder = orderDetails?.order_source === "customer";
    const platformFeePercent = isCustomerOrder
      ? (orderDetails?.marketplace_fee_percentage !== undefined && orderDetails?.marketplace_fee_percentage !== null
        ? parseFloat(orderDetails.marketplace_fee_percentage) || 0
        : parseFloat(vendorData?.platform_fee) || 0)
      : parseFloat(vendorData?.platform_fee) || 0;
    const serviceTaxPercent = parseFloat(vendorData?.service_tax) || 0;

    // Apply discount on combined total
    const discountTotalAmount = calculateDiscount(
      cartItemTotal,
      mode === "percentage" ? "percent" : "fixed",
      mode === "percentage"
        ? (selectedDiscount ?? customValue) !== undefined
          ? `${selectedDiscount ?? customValue}`
          : undefined
        : selectedDiscount ?? customValue
    );

    const afterDiscountTotal = cartItemTotal - discountTotalAmount;

    // Tax & Platform fee applied after discount
    const tax = (afterDiscountTotal * serviceTaxPercent) / 100;
    let platformFee = (afterDiscountTotal * platformFeePercent) / 100;

    if (isCustomerOrder) {
      const minCap = orderDetails?.marketplace_fee_min_cap;
      const maxCap = orderDetails?.marketplace_fee_max_cap;
      if (minCap !== null && minCap !== undefined && minCap !== "") {
        const min = parseFloat(minCap);
        if (!isNaN(min) && platformFee < min) {
          platformFee = min;
        }
      }
      if (maxCap !== null && maxCap !== undefined && maxCap !== "") {
        const max = parseFloat(maxCap);
        if (!isNaN(max) && platformFee > max) {
          platformFee = max;
        }
      }
    }

    // Final order total (for DB)
    let orderTotal = afterDiscountTotal + tax + platformFee;

    // if (orderDetails?.order_type === "delivery") {
    //   const riderTipAmount = parseFloat(orderDetails?.rider_tip_amount) || 0;
    //   orderTotal += riderTipAmount;

    //   const deliveryFee = parseFloat(orderDetails?.delivery_fee) || 0;
    //   if (deliveryFee > 0) {
    //     orderTotal += deliveryFee;
    //   }
    // }

    const packageCharge = parseFloat(orderDetails?.package_charge) || 0;
    const isChargesRemoved = !!(orderDetails?.is_charges_removed || orderDetails?.ischargesremoved);
    if (packageCharge > 0 && !isChargesRemoved) {
      orderTotal += packageCharge;
    }

    const roundedOrderTotal = Math.floor(orderTotal * 100) / 100;

    const body = {
      discount_type: mode === "percentage" ? "percent" : "fixed",
      discount_value: mode === "percentage"
        ? (selectedDiscount ?? customValue) !== undefined
          ? `${selectedDiscount ?? customValue}`
          : undefined
        : selectedDiscount ?? customValue,
      discount_amount: discountAmount,
      total_amount: parseFloat(roundedOrderTotal).toFixed(2)
    }

    const res = await addDiscountOrderAPI({
      id: orderDetails?.id,
      body
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      className="merge-table-dialog-container"
      PaperProps={{
        sx: {
          borderRadius: "20px",
          backgroundColor: "white",
          width: "490px",
        },
      }}
      TransitionProps={{
        onEntered: (node) => {
          focusFirstField(node);
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "center",
          padding: "25px",
        }}
      >
        Adding discount for #{orderDetails?.order_id}
      </DialogTitle>

      <DialogContent>
        {/* Tabs for Percentage / Amount */}
        <Box className="d-flex justify-content-center" mb={2}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, val) => val && setMode(val)}
            sx={{
              height: "32px",
              border: "1px solid #1297DE",
              borderRadius: "8px",
              "& .MuiToggleButton-root.Mui-selected": {
                backgroundColor: "rgba(18, 151, 222, 1)",
                color: "white",
              },
              "& .MuiToggleButton-root.Mui-selected:hover": {
                backgroundColor: "rgba(18, 151, 222, 0.8)"
              }
            }}
          >
            <ToggleButton
              value="percentage"
              sx={{
                width: "150px",
                textTransform: "none",
                color: "#1297DE",
                border: "none",
              }}
            >
              Percentage
            </ToggleButton>
            <ToggleButton
              value="amount"
              sx={{
                width: "150px",
                textTransform: "none",
                color: "#1297DE",
                border: "none",
              }}
            >
              Amount
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Quick Discount Buttons */}
        <Stack direction="row" spacing={2} justifyContent="center" mb={2}>
          {mode === "percentage" &&
            percentageOptions.map((val) => {
              const isSelected = selectedDiscount === val;
              return (
                <Button
                  key={val}
                  variant={selectedDiscount === val ? "contained" : "outlined"}
                  onClick={() => {
                    setSelectedDiscount(val);
                    setCustomValue("");
                    setCustomValueInput("");
                  }}
                  sx={{
                    borderRadius: "50%",
                    height: "60px",
                    fontWeight: "600",
                    lineHeight: 1,
                    ...(isSelected
                      ? {
                        backgroundColor: "#2E7D32",
                        color: "#fff",
                        border: "1px solid #2E7D32",
                        "&:hover": {
                          backgroundColor: "#1b5e20",
                        },
                      }
                      : {
                        backgroundColor: "transparent",
                        color: "#2E7D32",
                        border: "1px solid #2E7D32",
                        "&:hover": {
                          backgroundColor: "rgba(46, 125, 50, 0.08)",
                        },
                      }),
                  }}
                >
                  {val}%
                </Button>
              )
            })}

          {mode === "amount" &&
            amountOptions.map((val) => {
              const isSelected = selectedDiscount === val;
              return (
                <Button
                  key={val}
                  variant={selectedDiscount === val ? "contained" : "outlined"}
                  onClick={() => {
                    setSelectedDiscount(val);
                    setCustomValue("");
                  }}
                  sx={{
                    borderRadius: "50px",
                    height: "60px",
                    fontWeight: "600",
                    lineHeight: 1,
                    ...(isSelected
                      ? {
                        backgroundColor: "#2E7D32",
                        color: "#fff",
                        border: "1px solid #2E7D32",
                        "&:hover": {
                          backgroundColor: "#1b5e20",
                        },
                      }
                      : {
                        backgroundColor: "transparent",
                        color: "#2E7D32",
                        border: "1px solid #2E7D32",
                        "&:hover": {
                          backgroundColor: "rgba(46, 125, 50, 0.08)",
                        },
                      }),
                  }}
                >
                  {currencySymbol(vendorData?.currency)}
                  {val}
                </Button>
              )
            })}
        </Stack>

        <Box my={2} mt={3}>
          <CustomTypography
            variant={"body2"}
            label={"OR add custom amount"}
            sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}
          />
        </Box>

        {/* Custom Discount Input */}
        <Stack direction="row" spacing={2} justifyContent="center" mb={1}>
          <CustomTextField
            name={"value"}
            placeholder={mode === "percentage" ? "Value %" : `Value ${currencySymbol(vendorData?.currency)}`}
            value={customValueInput}
            // onChange={(e) => {
            //     setCustomValue(e.target.value);
            //     setSelectedDiscount(null);
            // }}
            onChange={(e) => {
              const val = e.target.value;

              // allow only digits and one dot
              if (/^\d*\.?\d*$/.test(val)) {
                setCustomValueInput(val);
                // setSelectedDiscount(null);
              }
            }}
            inputProps={{
              inputMode: "decimal",   // mobile keyboard shows numbers with dot
              pattern: "[0-9]*[.]?[0-9]*", // extra safeguard
            }}
            sx={{ width: '90px' }}
          />
          <CustomButton
            onClick={() => {
              // const value = Number(customValueInput);

              // // Validation for fixed amount
              // if (discountAmount > orderItemTotal) {
              //   return; // optionally show a toast/message here
              // }
              setCustomValue(customValueInput);
              setSelectedDiscount(null);
              setDiscountError("");
            }}
            sx={{ width: '50px', borderRadius: '10px', height: '44px' }}
          >
            ADD
          </CustomButton>
        </Stack>
        {discountError && (
          <CustomTypography
            label={discountError}
            color="error"
            variant="caption"
            sx={{ display: "block", textAlign: "center", mb: 3 }}
          />
        )}

        <Divider />

        {/* Calculation Section */}
        <Box mt={2}>
          <CustomTypography
            variant={"body2"}
            label={
              "The discount applies to the item total only. Taxes and other charges are calculated based on the discounted amount."
            }
            sx={{
              fontSize: "14px",
              fontWeight: "500",
              fontStyle: "italic",
              color: "#A1A1A1",
            }}
          />
        </Box>
        <Box mt={2}>
          {/* Order Total */}
          <Box className="d-flex align-items-center" sx={{ mb: 1 }}>
            <CustomTypography
              variant="body2"
              label="Order Items Total"
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                whiteSpace: "nowrap",
                mb: 0,
                color: "#919191",
              }}
            />

            <Box sx={{ flex: 1, borderBottom: "2px dotted #919191", mx: 2 }} />

            <CustomTypography
              variant="body2"
              label={`${currencySymbol(vendorData?.currency)}${orderItemTotal?.toFixed(2)}`}
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                mb: 0,
              }}
            />
          </Box>

          {/* Discount */}
          <Box className="d-flex align-items-center" sx={{ mb: 1 }}>
            <CustomTypography
              variant="body2"
              label={
                mode === "percentage"
                  ? `Discount (${selectedDiscount || customValue}%)`
                  : "Discount"
              }
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                whiteSpace: "nowrap",
                mb: 0,
                color: "#2E7D32",
              }}
            />

            <Box sx={{ flex: 1, borderBottom: "2px dotted #2E7D32", mx: 2 }} />

            <CustomTypography
              variant="body2"
              label={`- ${currencySymbol(vendorData?.currency)}${discountAmount.toFixed(2)}`}
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                mb: 0,
                color: "#2E7D32",
              }}
            />
          </Box>

          {/* Final Total */}
          <Box className="d-flex align-items-center" sx={{ mb: 1 }}>
            <CustomTypography
              variant="body2"
              label={`Total After Discount`}
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                whiteSpace: "nowrap",
                mb: 0,
              }}
            />

            <Box sx={{ flex: 1, borderBottom: "2px dotted #111111", mx: 2 }} />

            <CustomTypography
              variant="body2"
              label={`${currencySymbol(vendorData?.currency)}${finalTotal.toFixed(2)}`}
              sx={{
                fontSize: "18px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                mb: 0,
              }}
            />
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          className="d-flex align-items-center justify-content-center"
          mt={3}
          mb={2}
          gap={2}
        >
          <CustomButton
            onClick={onClose}
            sx={{
              backgroundColor: "transparent",
              color: "black",
              border: "1px solid black",
              borderRadius: "10px",
              width: "100px",
              height: "45px",
            }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            onClick={handleApply}
            sx={{ borderRadius: "10px", width: "220px", height: "45px" }}
          >
            Add Discount to Order
          </CustomButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DiscountModal;
